import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";

interface RouterContextType {
  currentPage: string;
  params: Record<string, string>;
  navigate: (page: string, params?: Record<string, string>) => void;
}

const RouterContext = createContext<RouterContextType | undefined>(undefined);

/**
 * Parse URL pathname and extract page and params
 * Examples:
 * / -> { page: 'home', params: {} }
 * /articles -> { page: 'articles', params: {} }
 * /articles/my-slug -> { page: 'article', params: { slug: 'my-slug' } }
 * /projects/my-project -> { page: 'project', params: { slug: 'my-project' } }
 */
function parseUrl(pathname: string): {
  page: string;
  params: Record<string, string>;
} {
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    return { page: "home", params: {} };
  }

  const firstSegment = segments[0];

  // Handle single-level routes
  if (segments.length === 1) {
    return { page: firstSegment, params: {} };
  }

  // Handle dynamic routes with slugs
  if (segments.length === 2) {
    const [collection, slug] = segments;
    // Convert plural to singular for page name
    const pageName =
      collection === "articles"
        ? "article"
        : collection === "projects"
        ? "project"
        : collection === "resources"
        ? "resource"
        : collection;
    return { page: pageName, params: { slug } };
  }

  return { page: firstSegment, params: {} };
}

/**
 * Build URL path from page and params
 */
function buildUrl(page: string, params?: Record<string, string>): string {
  if (page === "home") {
    return "/";
  }

  // Handle detail pages (article, project, resource)
  if (params?.slug) {
    if (page === "article") {
      return `/articles/${params.slug}`;
    } else if (page === "project") {
      return `/projects/${params.slug}`;
    } else if (page === "resource") {
      return `/resources/${params.slug}`;
    }
  }

  // Handle collection pages
  return `/${page}`;
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [currentPage, setCurrentPage] = useState("home");
  const [params, setParams] = useState<Record<string, string>>({});

  // Initialize from current URL
  useEffect(() => {
    const { page, params: urlParams } = parseUrl(window.location.pathname);
    setCurrentPage(page);
    setParams(urlParams);
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const { page, params: urlParams } = parseUrl(window.location.pathname);
      setCurrentPage(page);
      setParams(urlParams);
      window.scrollTo(0, 0);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = (page: string, newParams?: Record<string, string>) => {
    const url = buildUrl(page, newParams);
    window.history.pushState({}, "", url);
    setCurrentPage(page);
    setParams(newParams || {});
    window.scrollTo(0, 0);
  };

  return (
    <RouterContext.Provider value={{ currentPage, params, navigate }}>
      {children}
    </RouterContext.Provider>
  );
}

export function useRouter() {
  const context = useContext(RouterContext);
  if (!context) {
    throw new Error("useRouter must be used within RouterProvider");
  }
  return context;
}
